/**
 * Shared plumbing for everything attached to UIScene: modal stack, tracked listeners (all removed on
 * scene shutdown so 10 round-trips never grow listener counts) and a few scene-level helpers.
 */
import Phaser from 'phaser';
import { bus, Events, type EventPayloads } from '@/core/events';
import { ctxOf, type GameContext } from '@/core/context';
import { SceneKeys } from '@/core/scenes/keys';

export type Disposer = () => void;

export class UiRoot {
  private readonly disposers: Disposer[] = [];
  private readonly modals: string[] = [];
  private disposed = false;

  constructor(readonly scene: Phaser.Scene) {
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.dispose());
  }

  get ctx(): GameContext | null {
    try { return ctxOf(this.scene) ?? null; } catch { return null; }
  }

  // ------------------------------------------------------------------ modal stack
  get modal(): string | null { return this.modals.length ? (this.modals[this.modals.length - 1] ?? null) : null; }
  hasModal(name?: string): boolean { return name ? this.modals.includes(name) : this.modals.length > 0; }
  pushModal(name: string): void {
    if (!this.modals.includes(name)) this.modals.push(name);
    bus.emitTyped(Events.UI_OPEN, { panel: name });
  }
  popModal(name: string): void {
    const i = this.modals.indexOf(name);
    if (i >= 0) this.modals.splice(i, 1);
    bus.emitTyped(Events.UI_CLOSE, { panel: name });
  }

  // ------------------------------------------------------------------ tracked listeners
  track(d: Disposer): Disposer {
    this.disposers.push(d);
    return () => { const i = this.disposers.indexOf(d); if (i >= 0) this.disposers.splice(i, 1); d(); };
  }
  onBus<K extends keyof EventPayloads>(event: K, fn: (p: EventPayloads[K]) => void): Disposer {
    bus.onTyped(event, fn);
    return this.track(() => bus.offTyped(event, fn));
  }
  onKey(key: string, fn: (e: KeyboardEvent) => void): Disposer {
    const kb = this.scene.input.keyboard;
    if (!kb) return () => { /* no keyboard */ };
    const name = `keydown-${key}`;
    kb.on(name, fn);
    return this.track(() => kb.off(name, fn));
  }
  onKeyUp(key: string, fn: (e: KeyboardEvent) => void): Disposer {
    const kb = this.scene.input.keyboard;
    if (!kb) return () => { /* no keyboard */ };
    const name = `keyup-${key}`;
    kb.on(name, fn);
    return this.track(() => kb.off(name, fn));
  }
  onUpdate(fn: (time: number, delta: number) => void): Disposer {
    this.scene.events.on(Phaser.Scenes.Events.UPDATE, fn);
    return this.track(() => this.scene.events.off(Phaser.Scenes.Events.UPDATE, fn));
  }
  onPointer(event: string, fn: (...args: unknown[]) => void): Disposer {
    this.scene.input.on(event, fn);
    return this.track(() => this.scene.input.off(event, fn));
  }

  /** The gameplay scene under the UI (null while none is running). */
  worldScene(): Phaser.Scene | null {
    const scenes = this.scene.scene.manager.getScenes(true).filter((s) => s.scene.key !== SceneKeys.UI);
    return scenes[0] ?? null;
  }

  reduceMotion(): boolean { return this.ctx?.settings.reduceMotion ?? false; }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const d of this.disposers.splice(0)) { try { d(); } catch (e) { console.warn('[ui] disposer threw', e); } }
    this.modals.length = 0;
  }
}

/** Returns the UiRoot installed on the UIScene by attachUi (null before attach). */
export function uiRootOf(scene: Phaser.Scene): UiRoot | null {
  const ui = scene.scene.get(SceneKeys.UI) as (Phaser.Scene & { uiRoot?: UiRoot }) | null;
  return ui?.uiRoot ?? null;
}
